import * as THREE from "three";
import type { Settings } from "./Settings";
import type { PlaybackState, MoveContext } from "./PlaybackState";
import { NoSolutionsAvailableError } from "../playback/TourSource";
import type { TourData, TourSource } from "../playback/TourSource";
import type { SceneManager } from "../scene/SceneManager";
import type { KnightActor } from "../scene/KnightActor";
import { PlaceholderKnight } from "../scene/PlaceholderKnight";
import { CatfishActor, KoiActor } from "../scene/KoiActor";
import { DuckFamilyActor, SingleDuckActor } from "../scene/DuckFamilyActor";
import { TurtleActor } from "../scene/TurtleActor";
import { sampleJumpPose } from "../animation/JumpAnimation";
import { computeChaseCameraTarget, computeWideCompletionTarget } from "../animation/CameraAnimation";
import type { TourSonifier } from "../audio/TourSonifier";

/**
 * "empty" — nothing to play yet (e.g. an empty database): an unobtrusive
 * waiting state, not an error. "transport" — a real failure (network,
 * malformed response, HTTP error): shown as a non-intrusive warning with
 * bounded-backoff retry (section 30).
 */
export type PlaybackErrorKind = "empty" | "transport" | null;

export interface OverlaySnapshot {
  state: PlaybackState;
  tour: TourData | null;
  /** Count of completed path moves, 0..63. The closing edge is separate. */
  moveNumber: number;
  moveTotal: number;
  isClosingJump: boolean;
  errorMessage: string | null;
  errorKind: PlaybackErrorKind;
}

const FIXED_STATE_DURATIONS_MS: Partial<Record<PlaybackState, number>> = {
  PREPARING_JUMP: 90,
  COMPLETING_TOUR: 900,
  DISPLAYING_COMPLETION: 1400,
};

const INITIAL_RETRY_DELAY_MS = 500;
const MAX_RETRY_DELAY_MS = 8000;
const BOARD_CENTER = new THREE.Vector3(0, 0, 0);

/**
 * Drives every state in section 19 via `update(dt)`, called once per
 * rendered frame from `CoinArtApp`'s `requestAnimationFrame` loop. No
 * `setTimeout`/promise chain ever schedules an animation step; the only
 * asynchronous work is fetching tour data, and even that resolves into a
 * plain field (`nextTourReady`) that the next `update()` tick picks up —
 * there is no recursive "then animate then fetch then animate" chain.
 */
export class PlaybackController {
  private state: PlaybackState = "LOADING_TOUR";
  private stateElapsedMs = 0;
  private tour: TourData | null = null;
  private moves: MoveContext[] = [];
  private moveCursor = 0;
  private visitedCount = 0;
  private knight: KnightActor;
  /** Incremented once per tour (see `tickResettingBoard`); selects this tour's position in the color palette. */
  private tourIndex = -1;
  private performerKind: "knight"|"koi"|"catfish"|"turtle"|"duck"|"duck-family";

  private requestInFlight = false;
  private pendingNextTour = false;
  private nextTourReady: TourData | null = null;
  private retryDelayMs = INITIAL_RETRY_DELAY_MS;
  private lastErrorMessage: string | null = null;
  private lastErrorKind: PlaybackErrorKind = null;
  private pausedFromState: PlaybackState | null = null;
  private resetPrepared = false;

  constructor(private scene: SceneManager, private tourSource: TourSource, private settings: Settings, private sonifier?: TourSonifier) {
    this.knight = new PlaceholderKnight();
    this.performerKind = settings.performer;
    this.scene.setKnight(this.knight);
    settings.onChange(()=>this.syncPerformer());
  }

  getSnapshot(): OverlaySnapshot {
    const move = this.moves[this.moveCursor] ?? null;
    const isClosingJump =
      (this.state === "ORIENTING_KNIGHT" ||
        this.state === "POSITIONING_CAMERA" ||
        this.state === "PREPARING_JUMP" ||
        this.state === "JUMPING" ||
        this.state === "LANDING") &&
      (move?.isClosingMove ?? false);

    return {
      state: this.state,
      tour: this.tour,
      moveNumber: Math.max(0, this.visitedCount - 1),
      moveTotal: 63,
      isClosingJump,
      errorMessage: this.lastErrorMessage,
      errorKind: this.lastErrorKind,
    };
  }

  update(dtSeconds: number): void {
    if (this.settings.paused) {
      if (this.state !== "PAUSED") {
        this.pausedFromState = this.state;
        this.state = "PAUSED";
      }
      return;
    }
    if (this.state === "PAUSED" && this.pausedFromState) {
      this.state = this.pausedFromState;
      this.pausedFromState = null;
    }

    this.stateElapsedMs += dtSeconds * 1000;

    switch (this.state) {
      case "LOADING_TOUR":
        this.tickLoadingTour();
        break;
      case "RESETTING_BOARD":
        this.tickResettingBoard();
        break;
      case "ORIENTING_KNIGHT":
        this.tickOrientingKnight();
        break;
      case "POSITIONING_CAMERA":
        this.tickPositioningCamera(dtSeconds);
        break;
      case "PREPARING_JUMP":
        this.tickFixedDuration("PREPARING_JUMP", "JUMPING");
        break;
      case "JUMPING":
        this.tickJumping(dtSeconds);
        break;
      case "LANDING":
        this.tickLanding(dtSeconds);
        break;
      case "ADVANCING":
        this.tickAdvancing();
        break;
      case "COMPLETING_TOUR":
        this.tickCompletingTour(dtSeconds);
        break;
      case "DISPLAYING_COMPLETION":
        this.tickDisplayingCompletion(dtSeconds);
        break;
      case "LOADING_NEXT_TOUR":
        this.tickLoadingNextTour();
        break;
      case "ERROR":
        this.tickError();
        break;
      case "PAUSED":
        break;
    }
  }

  /** Forces an immediate switch to a fresh tour, used by the "Next tour" control. */
  skipToNextTour(): void {
    this.pendingNextTour = false;
    this.nextTourReady = null;
    // A manual skip explicitly requests another stored answer. Automatic
    // playback of a closed tour repeats its unchanged sequence instead.
    this.tour = null;
    this.transition("LOADING_TOUR");
  }

  resetCamera(): void {
    const target = this.cameraPresetTarget();
    this.scene.cameraRig.snapTo(target.position, target.lookAt);
  }

  dispose(): void {
    this.knight.dispose();
  }

  // -- state entry side effects --------------------------------------------

  private transition(next: PlaybackState): void {
    this.state = next;
    this.stateElapsedMs = 0;
    if (next === "RESETTING_BOARD") this.resetPrepared = false;
    this.onEnter(next);
  }

  private onEnter(state: PlaybackState): void {
    switch (state) {
      case "LANDING": {
        const move = this.currentMove();
        // No flash/glow on landing by design — the permanent color snap
        // below is the only visual change, no separate emphasis effect.
        if (move && !move.isClosingMove) {
          this.visitedCount = move.visitOrder;
          // Assigned exactly once, as an immediate snap to this square's
          // family visited color — not a function of visit order. This
          // square's color is now permanent until the next
          // resetBoardColors() — nothing later revisits or re-tints it.
          this.scene.board.applyPermanentVisitColor(move.toSquare);
          this.scene.board.setTourProgress?.(this.visitedCount);
          this.scene.tourPath?.update(this.visitedCount);
          this.scene.showLandingRipple?.(move.toSquare);
          this.sonifier?.landing(move.toSquare);
        }
        break;
      }
      case "COMPLETING_TOUR":
        this.sonifier?.complete(this.tour?.isClosed ?? false);
        // Snap every tile to its family's exact endpoint color (red/blue)
        // before the completion pulse plays, per spec: the completed board
        // must be exactly two endpoint colors, not whatever the last
        // computed lerp rounded to.
        this.scene.board.normalizeCompletedBoard();
        // Prefetch now so the next tour is likely ready before the
        // completion hold finishes, instead of blocking on it later.
        this.prefetchNextTour();
        break;
      case "ADVANCING":
        this.moveCursor += 1;
        break;
      default:
        break;
    }
  }

  // -- per-state ticks -------------------------------------------------------

  private tickLoadingTour(): void {
    if (this.requestInFlight) return;
    this.requestInFlight = true;
    this.tourSource
      .next({ excludeId: this.tour?.id })
      .then((tour) => {
        this.tour = tour;
        this.moves = this.buildMoves(tour);
        this.moveCursor = 0;
        this.visitedCount = 0;
        this.retryDelayMs = INITIAL_RETRY_DELAY_MS;
        this.lastErrorMessage = null;
        this.lastErrorKind = null;
        this.transition("RESETTING_BOARD");
      })
      .catch((error: unknown) => {
        this.lastErrorMessage = error instanceof Error ? error.message : "failed to load a tour";
        this.lastErrorKind = error instanceof NoSolutionsAvailableError ? "empty" : "transport";
        this.transition("ERROR");
      })
      .finally(() => {
        this.requestInFlight = false;
      });
  }

  private tickResettingBoard(): void {
    if (!this.resetPrepared) {
      this.resetPrepared = true;
      this.tourIndex += 1;
      this.scene.board.resetBoardColors(this.tourIndex);
      if (this.tour) {
      const startSquare = this.tour.tour[0];
      this.visitedCount = 1;
      this.scene.tourPath?.setTour(this.tour.tour, this.tour.isClosed);
      this.scene.showTourStart?.(startSquare, this.tourIndex > 0 && !this.tour.isClosed);
      this.sonifier?.cue(this.tourIndex > 0 && !this.tour.isClosed);
      // The starting square is assigned before the first jump.
      this.scene.board.applyPermanentVisitColor(startSquare);
      this.scene.board.setTourProgress?.(1);
      const waterY=this.actorWaterY();
      const startPos = this.scene.squareWorldPosition(startSquare, this.settings.sceneMode === "aquarium" && this.settings.performer !== "knight" ? waterY : 0);
      this.knight.setPosition(startPos);
      // Start each tour from the same wide, whole-board composition used
      // for DISPLAYING_COMPLETION, not a tight shot of the first square.
      this.resetCamera();
      }
    }
    const cueHoldMs = this.settings.showStartCue ? 1250 : 0;
    if (this.stateElapsedMs >= cueHoldMs) this.transition("ORIENTING_KNIGHT");
  }

  private tickOrientingKnight(): void {
    const move = this.currentMove();
    if (!move) {
      this.transition("COMPLETING_TOUR");
      return;
    }
    const aquariumKoi = this.settings.sceneMode === "aquarium" && this.settings.performer !== "knight";
    const waterY=this.actorWaterY();
    const start = this.scene.squareWorldPosition(move.fromSquare, aquariumKoi ? waterY : 0);
    const end = this.scene.squareWorldPosition(move.toSquare, aquariumKoi ? waterY : 0);
    this.knight.setYaw(sampleJumpPose(start, end, 0).yaw);
    const orientDuration=this.isDuck()?Math.max(360,this.settings.timing.anticipationMs*.8):this.settings.timing.anticipationMs*.3;
    (this.knight as KnightActor & {setTurnPose?:(progress:number)=>void}).setTurnPose?.(Math.min(1,this.stateElapsedMs/orientDuration));

    if (this.stateElapsedMs >= orientDuration) {
      this.transition("POSITIONING_CAMERA");
    }
  }

  private tickPositioningCamera(dtSeconds: number): void {
    const move = this.currentMove();
    if (!move) {
      this.transition("COMPLETING_TOUR");
      return;
    }
    const aquariumKoi = this.settings.sceneMode === "aquarium" && this.settings.performer !== "knight";
    const waterY=this.actorWaterY();
    const start = this.scene.squareWorldPosition(move.fromSquare, aquariumKoi ? waterY : 0);
    const end = this.scene.squareWorldPosition(move.toSquare, aquariumKoi ? waterY : 0);
    const target = this.cameraTarget(start, end, 0);
    this.scene.cameraRig.followTarget(target.position, target.lookAt, dtSeconds, 5);
    const positionDuration=this.isDuck()?Math.max(280,this.settings.timing.anticipationMs*.9):this.settings.timing.anticipationMs*.7;
    if(this.isDuck()){
      (this.knight as KnightActor & {setTurnPose?:(progress:number)=>void}).setTurnPose?.(.58+.42*Math.min(1,this.stateElapsedMs/positionDuration));
    }

    if (this.stateElapsedMs >= positionDuration) {
      this.transition("PREPARING_JUMP");
    }
  }

  private tickFixedDuration(current: PlaybackState, next: PlaybackState): void {
    if (this.stateElapsedMs >= (FIXED_STATE_DURATIONS_MS[current] ?? 0)) {
      this.transition(next);
    }
  }

  private tickJumping(dtSeconds: number): void {
    const move = this.currentMove();
    if (!move) {
      this.transition("COMPLETING_TOUR");
      return;
    }
    const aquariumKoi = this.settings.sceneMode === "aquarium" && this.settings.performer !== "knight";
    const waterY=this.actorWaterY();
    const start = this.scene.squareWorldPosition(move.fromSquare, aquariumKoi ? waterY : 0);
    const end = this.scene.squareWorldPosition(move.toSquare, aquariumKoi ? waterY : 0);
    const jumpDuration=this.settings.performer==="turtle"?Math.max(1050,this.settings.timing.jumpMs*2.6):this.isDuck()?Math.max(520,this.settings.timing.jumpMs*1.25):this.settings.timing.jumpMs;
    const progress = Math.min(this.stateElapsedMs / jumpDuration, 1);

    const pose = sampleJumpPose(start, end, progress);
    if (this.settings.performer !== "knight") {
      // Knight moves always change square colour. A dark destination dives;
      // a light destination breaches above the water before settling.
      const destinationDark = ((Math.floor(move.toSquare/8)+(move.toSquare%8))&1)===1;
      const duckOnLand=(this.isDuck()||this.settings.performer==="turtle")&&this.settings.sceneMode!=="aquarium";
      if (this.settings.sceneMode === "aquarium") {
        // Two distinct cubic-like arcs: the dive accelerates under the
        // surface and rises softly; the breach climbs steeply, hangs above
        // the water, then curves back down without changing its endpoint.
        const s = Math.sin(Math.PI*progress);
        const hang = Math.sin(Math.PI*Math.min(1, Math.pow(progress,.72)));
        pose.position.y = this.settings.performer==="turtle"
          ? .48+.018*Math.sin(progress*Math.PI*2)
          : this.isDuck()? .64+.10*s : .34 + (destinationDark ? -.38*s*(.72+.28*Math.sin(Math.PI*progress)) : .91*hang);
        // A restrained S-curve in the horizontal plane gives the fish room
        // to swim around the straight knight vector while preserving both
        // exact endpoints.
        const dx=end.x-start.x, dz=end.z-start.z, length=Math.hypot(dx,dz)||1;
        // sin(pi*t) * sin(2*pi*t) guarantees an exact zero offset at both
        // square centres. Ducks use a gentler S than diving fish.
        const lateral=(this.settings.performer==="turtle"?.035:this.isDuck()?.072:.12)*s*Math.sin(progress*Math.PI*2);
        pose.position.x += (-dz/length)*lateral;
        pose.position.z += (dx/length)*lateral;
      } else if(duckOnLand){
        // A brisk waddling run: no airborne arc. The tiny lateral footfall
        // oscillation is enveloped to zero at both square centres.
        const s=Math.sin(Math.PI*progress);
        pose.position.y=.035+Math.abs(Math.sin(progress*Math.PI*6))*.018;
        const dx=end.x-start.x,dz=end.z-start.z,length=Math.hypot(dx,dz)||1;
        const waddle=(this.settings.performer==="turtle"?.018:.035)*s*Math.sin(progress*Math.PI*6);
        pose.position.x+=(-dz/length)*waddle;
        pose.position.z+=(dx/length)*waddle;
      } else {
        pose.position.y += destinationDark ? -Math.sin(Math.PI*progress)*.48 : Math.sin(Math.PI*progress)*.48;
      }
      const wave = Math.sin(progress*Math.PI*4.2);
      const envelope = Math.sin(Math.PI*progress);
      const verticalSlope = this.settings.performer==="turtle"?.025*Math.sin(progress*Math.PI*2):duckOnLand?.055*Math.sin(progress*Math.PI*6):this.isDuck()?-.16*Math.cos(Math.PI*progress):destinationDark?.78*Math.cos(Math.PI*progress):-1.08*Math.cos(Math.PI*progress);
      const bank = this.settings.performer==="turtle"?.04*Math.sin(progress*Math.PI*2):duckOnLand?.11*Math.sin(progress*Math.PI*6):Math.sin(Math.PI*progress)*(this.isDuck()?.06:.24)*Math.sin(progress*Math.PI*2);
      const articulated = this.knight as KnightActor & {setSwimPose?:(pitch:number,roll:number,bend:number,tail:number,fin:number)=>void};
      articulated.setSwimPose?.(verticalSlope, bank, wave*.52*envelope, Math.sin(progress*Math.PI*9), .72+.22*envelope);
    }
    this.knight.setPosition(pose.position);
    this.knight.setYaw(pose.yaw);

    // Tile colors are not touched here: a tile's permanent color is
    // assigned exactly once, on landing (see onEnter "LANDING") or at the
    // start of the tour for the first square (see tickResettingBoard).
    // Updating it continuously here would mean every unvisited tile keeps
    // changing color as the tour progresses, which is exactly the bug
    // this was rewritten to fix.
    const target = this.cameraTarget(start, end, progress);
    this.scene.cameraRig.followTarget(target.position, target.lookAt, dtSeconds, 8);

    if (progress >= 1) this.transition("LANDING");
  }

  private syncPerformer():void {
    if (this.performerKind===this.settings.performer) return;
    const previous=this.knight.object.position.clone();
    this.knight.dispose();
    this.knight=this.settings.performer==="koi"?new KoiActor():this.settings.performer==="catfish"?new CatfishActor():this.settings.performer==="turtle"?new TurtleActor():this.settings.performer==="duck"?new SingleDuckActor():this.settings.performer==="duck-family"?new DuckFamilyActor():new PlaceholderKnight();
    this.performerKind=this.settings.performer;
    this.scene.setKnight(this.knight); this.knight.setPosition(previous);
  }

  private isDuck():boolean{return this.settings.performer==="duck"||this.settings.performer==="duck-family"}
  private actorWaterY():number{return this.settings.performer==="turtle"?.48:this.isDuck()?.64:.34}

  private tickLanding(dtSeconds: number): void {
    const move = this.currentMove();
    if (move) {
      // Same wide chase framing as the end of JUMPING (focusT=1), not a
      // separate tight close-up — keeps the whole board in view instead
      // of the camera lunging in right as the knight lands.
      const start = this.scene.squareWorldPosition(move.fromSquare);
      const end = this.scene.squareWorldPosition(move.toSquare);
      const target = this.cameraTarget(start, end, 1);
      this.scene.cameraRig.followTarget(target.position, target.lookAt, dtSeconds, 4);
    }
    if (this.stateElapsedMs >= this.settings.timing.landingMs) {
      if (move?.isClosingMove) this.transition("COMPLETING_TOUR");
      else this.transition("ADVANCING");
    }
  }

  private tickAdvancing(): void {
    if (this.moveCursor < this.moves.length) this.transition("ORIENTING_KNIGHT");
    else this.transition("COMPLETING_TOUR");
  }

  private tickCompletingTour(dtSeconds: number): void {
    const duration = FIXED_STATE_DURATIONS_MS.COMPLETING_TOUR ?? 900;
    const t = Math.min(this.stateElapsedMs / duration, 1);
    this.scene.board.setCompletionPulse(Math.sin(Math.PI * t) * 0.9);

    const wide = computeWideCompletionTarget(BOARD_CENTER);
    this.scene.cameraRig.followTarget(wide.position, wide.lookAt, dtSeconds, 2.2);

    if (t >= 1) {
      this.scene.board.clearPulse();
      this.transition("DISPLAYING_COMPLETION");
    }
  }

  private tickDisplayingCompletion(dtSeconds: number): void {
    const wide = computeWideCompletionTarget(BOARD_CENTER);
    this.scene.cameraRig.followTarget(wide.position, wide.lookAt, dtSeconds, 3);

    if (this.stateElapsedMs >= (FIXED_STATE_DURATIONS_MS.DISPLAYING_COMPLETION ?? 1400)) {
      this.transition("LOADING_NEXT_TOUR");
    }
  }

  private tickLoadingNextTour(): void {
    if (this.nextTourReady) {
      const tour = this.nextTourReady;
      this.nextTourReady = null;
      this.tour = tour;
      this.moves = this.buildMoves(tour);
      this.moveCursor = 0;
      this.visitedCount = 0;
      this.retryDelayMs = INITIAL_RETRY_DELAY_MS;
      this.lastErrorMessage = null;
      this.lastErrorKind = null;
      this.transition("RESETTING_BOARD");
      return;
    }
    if (this.lastErrorMessage) {
      this.transition("ERROR");
      return;
    }
    if (!this.pendingNextTour) this.prefetchNextTour();
  }

  private tickError(): void {
    if (this.stateElapsedMs >= this.retryDelayMs) {
      this.retryDelayMs = Math.min(this.retryDelayMs * 2, MAX_RETRY_DELAY_MS);
      this.transition("LOADING_TOUR");
    }
  }

  // -- helpers -----------------------------------------------------------

  private prefetchNextTour(): void {
    if (this.pendingNextTour) return;
    // Closedness is metadata: the legal item-64 -> item-1 edge is not
    // animated. After the completion hold, replay the exact stored sequence
    // from item 1; rotating the array would make its numbering misleading.
    if (this.tour?.isClosed) {
      this.nextTourReady = this.tour;
      return;
    }
    this.pendingNextTour = true;
    this.tourSource
      .next({
        excludeId: this.tour?.id,
        continueFromSquare: this.tour?.tour[this.tour.tour.length - 1],
      })
      .then((tour) => {
        this.nextTourReady = tour;
      })
      .catch((error: unknown) => {
        this.lastErrorMessage = error instanceof Error ? error.message : "failed to load the next tour";
        this.lastErrorKind = error instanceof NoSolutionsAvailableError ? "empty" : "transport";
      })
      .finally(() => {
        this.pendingNextTour = false;
      });
  }

  private currentMove(): MoveContext | null {
    return this.moves[this.moveCursor] ?? null;
  }

  private buildMoves(tour: TourData): MoveContext[] {
    const moves: MoveContext[] = [];
    for (let i = 0; i < tour.tour.length - 1; i++) {
      moves.push({
        fromSquare: tour.tour[i],
        toSquare: tour.tour[i + 1],
        visitOrder: i + 2,
        isClosingMove: false,
      });
    }
    return moves;
  }

  private cameraTarget(start: THREE.Vector3, end: THREE.Vector3, progress: number): { position: THREE.Vector3; lookAt: THREE.Vector3 } {
    if (this.settings.cameraMode === "follow" || this.settings.cameraMode === "cinematic") return computeChaseCameraTarget(start, end, progress);
    return this.cameraPresetTarget();
  }

  private cameraPresetTarget(): { position: THREE.Vector3; lookAt: THREE.Vector3 } {
    switch (this.settings.cameraMode) {
      case "top": return { position: new THREE.Vector3(0, 10.8, .01), lookAt: BOARD_CENTER.clone() };
      case "isometric": return { position: new THREE.Vector3(7.4, 8.2, 7.4), lookAt: BOARD_CENTER.clone() };
      case "orbit": {
        const angle = performance.now() * .00012;
        return { position: new THREE.Vector3(Math.cos(angle) * 9, 6.8, Math.sin(angle) * 9), lookAt: BOARD_CENTER.clone() };
      }
      default: return computeWideCompletionTarget(BOARD_CENTER);
    }
  }
}
